import { createFileRoute, Link } from '@tanstack/react-router';
import { Shield, Check, FileText, Globe, Lock, Building2 } from "lucide-react";
import { ContentHeader, ContentSection, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/legal/compliance')({
  component: CompliancePage,
  head: () => ({
    meta: [
      { title: 'Compliance | Matricks' },
      { name: 'description', content: 'Matricks compliance certifications and regulatory adherence including SOC 2, GDPR, HIPAA, and more.' },
    ],
  }),
});

const certifications = [
  {
    name: "SOC 2 Type II",
    icon: Shield,
    status: "Certified",
    description:
      "Annual audit verifying security, availability, and confidentiality controls. Report available under NDA.",
  },
  {
    name: "GDPR",
    icon: Globe,
    status: "Compliant",
    description:
      "Full compliance with EU General Data Protection Regulation including data subject rights and DPAs.",
  },
  {
    name: "HIPAA",
    icon: Building2,
    status: "Ready",
    description:
      "HIPAA-compliant features for healthcare organizations. Business Associate Agreements available.",
  },
  {
    name: "ISO 27001",
    icon: FileText,
    status: "In Progress",
    description:
      "Information security management system certification in progress. Expected Q2 2025.",
  },
  {
    name: "CCPA",
    icon: Lock,
    status: "Compliant",
    description:
      "California Consumer Privacy Act compliance for data privacy rights of California residents.",
  },
  {
    name: "PCI DSS",
    icon: Shield,
    status: "Level 1",
    description:
      "Payment card industry compliance through certified payment processors. No card data stored.",
  },
];

const faqItems = [
  {
    question: "Can I get a copy of your SOC 2 report?",
    answer:
      "Yes, our SOC 2 Type II report is available to customers and prospects under NDA. Contact our sales team to request a copy.",
  },
  {
    question: "Do you offer a Data Processing Agreement (DPA)?",
    answer:
      "Yes, we provide a GDPR-compliant DPA for all customers processing EU personal data. Enterprise customers can request a customized DPA.",
  },
  {
    question: "Can Matricks sign a Business Associate Agreement (BAA)?",
    answer:
      "Yes, BAAs are available for enterprise customers who need to process protected health information (PHI) in compliance with HIPAA.",
  },
  {
    question: "Where is my data stored?",
    answer:
      "By default, data is stored in secure data centers in the US. Enterprise customers can choose data residency in EU, US, or other regions based on compliance requirements.",
  },
  {
    question: "How do you handle data subject access requests?",
    answer:
      "We provide self-service tools for common data subject rights (access, deletion, portability). For complex requests, our support team assists with fulfillment within regulatory timeframes.",
  },
];

function CompliancePage() {
  return (
    <div className="py-16 sm:py-24">
      <div className="container px-6">
        <ContentHeader
          title="Compliance"
          description="Meeting the highest standards for security, privacy, and regulatory compliance."
          breadcrumbs={[
            { label: "Home", href: "/" },
            { label: "Legal", href: "/legal" },
            { label: "Compliance" },
          ]}
        />

        {/* Certifications Grid */}
        <ContentSection id="certifications" title="Certifications & Standards">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert) => (
              <div key={cert.name} className="rounded-xl border bg-card p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <cert.icon className="h-6 w-6 text-primary" />
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      cert.status === "Certified" || cert.status === "Compliant"
                        ? "bg-green-500/10 text-green-600"
                        : cert.status === "Ready" || cert.status === "Level 1"
                        ? "bg-blue-500/10 text-blue-600"
                        : "bg-yellow-500/10 text-yellow-600"
                    }`}
                  >
                    {cert.status}
                  </span>
                </div>
                <h3 className="font-semibold text-lg mb-2">{cert.name}</h3>
                <p className="text-muted-foreground text-sm">{cert.description}</p>
              </div>
            ))}
          </div>
        </ContentSection>

        {/* SOC 2 Details */}
        <ContentSection id="soc2" title="SOC 2 Type II" background="muted">
          <div className="space-y-6">
            <div className="rounded-xl border bg-card p-6">
              <p className="text-muted-foreground mb-4">
                Matricks maintains SOC 2 Type II certification, verified annually by an independent
                auditor. This certification demonstrates our commitment to the five Trust Service
                Criteria:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {[
                  { name: "Security", desc: "Protection against unauthorized access" },
                  { name: "Availability", desc: "System availability for operation" },
                  { name: "Processing Integrity", desc: "Complete and accurate processing" },
                  { name: "Confidentiality", desc: "Protection of confidential information" },
                  { name: "Privacy", desc: "Personal information handling" },
                ].map((criterion) => (
                  <div key={criterion.name} className="rounded-lg bg-muted/50 p-4 text-center">
                    <div className="h-8 w-8 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-2">
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                    <h4 className="font-medium text-sm">{criterion.name}</h4>
                    <p className="text-xs text-muted-foreground mt-1">{criterion.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold mb-4">Request SOC 2 Report</h3>
              <p className="text-muted-foreground mb-4">
                Our SOC 2 Type II report is available to customers and qualified prospects under
                NDA. The report includes:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li>Independent auditor&apos;s opinion</li>
                <li>Description of our security controls</li>
                <li>Test procedures and results</li>
                <li>Management&apos;s assertions</li>
              </ul>
              <div className="mt-4">
                <Link
                  to="/info/contact"
                  className="inline-flex items-center gap-2 text-primary hover:underline"
                >
                  Request report access
                </Link>
              </div>
            </div>
          </div>
        </ContentSection>

        {/* GDPR */}
        <ContentSection id="gdpr" title="GDPR Compliance">
          <div className="space-y-6">
            <div className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold text-lg mb-4">Our GDPR Commitment</h3>
              <p className="text-muted-foreground mb-4">
                Matricks is fully compliant with the General Data Protection Regulation (GDPR). We
                process personal data of EU residents lawfully, fairly, and transparently.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2">Data Subject Rights</h4>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li>Right to access personal data</li>
                    <li>Right to rectification</li>
                    <li>Right to erasure (&quot;right to be forgotten&quot;)</li>
                    <li>Right to data portability</li>
                    <li>Right to restrict processing</li>
                    <li>Right to object to processing</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Our Practices</h4>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li>Privacy by design and default</li>
                    <li>Data minimization principles</li>
                    <li>Lawful basis for processing</li>
                    <li>Breach notification within 72 hours</li>
                    <li>Data Protection Impact Assessments</li>
                    <li>Regular privacy training for staff</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </ContentSection>

        {/* HIPAA */}
        <ContentSection id="hipaa" title="HIPAA Compliance" background="muted">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold text-lg mb-4">Healthcare Compliance</h3>
            <p className="text-muted-foreground mb-4">
              Matricks provides HIPAA-compliant features for healthcare organizations handling
              protected health information (PHI).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-2">Technical Safeguards</h4>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>Access controls and user authentication</li>
                  <li>Automatic log-off and session management</li>
                  <li>Encryption of PHI in transit and at rest</li>
                  <li>Audit logging of all PHI access</li>
                  <li>Integrity controls for data accuracy</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Administrative Safeguards</h4>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>Workforce security training</li>
                  <li>Security incident procedures</li>
                  <li>Contingency planning</li>
                  <li>Business Associate Agreements</li>
                  <li>Risk analysis and management</li>
                </ul>
              </div>
            </div>
            <div className="mt-6 p-4 rounded-lg bg-muted/50">
              <h4 className="font-medium mb-2">Business Associate Agreement</h4>
              <p className="text-muted-foreground text-sm">
                Enterprise customers processing PHI can request a Business Associate Agreement
                (BAA). Contact our sales team to discuss your healthcare compliance requirements.
              </p>
            </div>
          </div>
        </ContentSection>

        {/* Data Residency */}
        <ContentSection id="data-residency" title="Data Residency">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold text-lg mb-4">Geographic Data Control</h3>
            <p className="text-muted-foreground mb-4">
              Enterprise customers can choose where their data is stored and processed to meet
              regulatory and compliance requirements.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-lg bg-muted/50 p-4 text-center">
                <h4 className="font-medium">United States</h4>
                <p className="text-muted-foreground text-sm mt-1">
                  Data centers in Virginia and Oregon
                </p>
              </div>
              <div className="rounded-lg bg-muted/50 p-4 text-center">
                <h4 className="font-medium">European Union</h4>
                <p className="text-muted-foreground text-sm mt-1">
                  Data centers in Ireland and Germany
                </p>
              </div>
              <div className="rounded-lg bg-muted/50 p-4 text-center">
                <h4 className="font-medium">Asia Pacific</h4>
                <p className="text-muted-foreground text-sm mt-1">
                  Data centers in Singapore and Sydney
                </p>
              </div>
            </div>
            <p className="text-muted-foreground text-sm mt-4">
              Data residency options are available on Enterprise plans. Contact sales for details.
            </p>
          </div>
        </ContentSection>

        {/* Compliance Reports */}
        <ContentSection id="reports" title="Compliance Documentation" background="muted">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold mb-4">Available Documents</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <span>SOC 2 Type II Report (under NDA)</span>
                </li>
                <li className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <span>Data Processing Agreement (DPA)</span>
                </li>
                <li className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <span>Business Associate Agreement (BAA)</span>
                </li>
                <li className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <span>Security Whitepaper</span>
                </li>
                <li className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <span>Penetration Test Summary</span>
                </li>
                <li className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <span>Sub-Processor List</span>
                </li>
              </ul>
            </div>
            <div className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold mb-4">Request Documentation</h3>
              <p className="text-muted-foreground mb-4">
                Compliance documentation is available for customers, qualified prospects, and their
                legal or security teams.
              </p>
              <p className="text-muted-foreground text-sm mb-4">
                To request documentation:
              </p>
              <ol className="space-y-2 text-muted-foreground text-sm list-decimal list-inside">
                <li>Contact our sales or security team</li>
                <li>Provide your organization details</li>
                <li>Sign required NDA (for some documents)</li>
                <li>Receive secure access to documentation</li>
              </ol>
              <div className="mt-4">
                <Link
                  to="/info/contact"
                  className="inline-flex items-center gap-2 text-primary hover:underline"
                >
                  Request documentation
                </Link>
              </div>
            </div>
          </div>
        </ContentSection>

        {/* FAQ */}
        <FAQSection items={faqItems} />

        {/* CTA */}
        <CTASection
          title="Need Compliance Information?"
          description="Our team is ready to help with your compliance and security questions."
          primaryAction={{ label: "Contact Us", href: "/info/contact" }}
          secondaryAction={{ label: "View Security", href: "/legal/security" }}
          background="primary"
        />
      </div>
    </div>
  );
}
